import {
  Button,
  DatePicker,
  Form,
  FormInstance,
  Image,
  Input,
  message,
  Modal,
  Select,
  Space,
  Upload,
} from "antd";
import { useState } from "react";
import type { UploadChangeParam, UploadFile } from "antd/lib/upload";
import { BusinessPerson } from "../../../../../interfaces/businessFull.interface";
import {
  PlusOutlined,
  MinusCircleOutlined,
  UploadOutlined,
  EyeOutlined,
} from "@ant-design/icons";
import styles from "../BusinessEditor.module.css";
import { Option } from "antd/lib/mentions";
import { getImageFromServer, uploadImageServer } from "../../../businessesApi";
import { RcFile } from "antd/lib/upload";
import {
  PERSON_IDENTITY_DOCUMENT_TYPE_OPTIONS,
  isTwoSidedDocumentType,
} from "./personIdentityDocumentValidation";
import { useAppSelector } from "../../../../../app/hooks";
import { selectCurrentBusiness } from "../../../businessesSlice";
import AdyenDocumentStateAlert from "../../Adyen-Documents/AdyenDocumentStateAlert";
import PersonAdyenDocuments from "../../Adyen-Documents/PersonAdyenDocuments";
import { useAdyenDocumentState } from "../../Adyen-Documents/useAdyenDocumentState";
const dateFormat = "DD/MM/YYYY";
const getBase64 = (file: RcFile): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = (error) => reject(error);
  });
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[A-Za-z]{2,}$/;

// Judges only an address the operator actually typed: a malformed one already
// stored on a person must not block an unrelated save, which is the whole point
// of dropping the required rules.
const personEmailRule = (form: FormInstance, name: number) => ({
  validator: (_: unknown, value?: string) => {
    if (!value || !form.isFieldTouched(["persons", name, "email"]))
      return Promise.resolve();
    return EMAIL_PATTERN.test(value)
      ? Promise.resolve()
      : Promise.reject(new Error("Invalid Email"));
  },
});

type AttachmentSide = "front" | "back";

const ATTACHMENT_FIELD: Record<AttachmentSide, keyof BusinessPerson> = {
  front: "identificationAttachment",
  back: "identificationAttachmentBack",
};

const ATTACHMENT_LABEL: Record<AttachmentSide, string> = {
  front: "Document (front side)",
  back: "Document (back side)",
};

// A valid identity document is a JPG/PNG image or a PDF, non-empty, within a
// sane size. These are the formats Adyen accepts for identity documents, so we
// keep the admin upload in lockstep to avoid uploads that fail acquirer-side.
const ALLOWED_DOCUMENT_TYPES = ["image/jpeg", "image/png", "application/pdf"];
const MAX_DOCUMENT_SIZE_MB = 10;

// Uploaded file names are "<uuid>.<ext>" (extension derived server-side from the
// file bytes), so the extension is a reliable signal of the document type.
const isPdfFileName = (fileName?: string) =>
  Boolean(fileName?.toLowerCase().endsWith(".pdf"));

export default function PersonsEditor({ form }: { form: FormInstance }) {
  // Subscribe to the persons value so the upload/preview controls re-render
  // after an upload mutates the form via setFieldsValue (otherwise the newly
  // uploaded side's preview button would not appear until the next re-render).
  Form.useWatch("persons", form);

  const currentBusiness = useAppSelector(selectCurrentBusiness);
  const { error: adyenDocumentsError, refetch: refetchAdyenDocuments } =
    useAdyenDocumentState(currentBusiness?._id);
  const [previewImage, setPreviewImage] = useState<string>("");
  const [previewOpen, setPreviewOpen] = useState(false);
  // PDFs can't render through antd <Image> (it renders an <img>), so they are
  // shown in a Modal with an <iframe> instead. Track which viewer to use.
  const [previewPdf, setPreviewPdf] = useState<string>("");

  const handleUpload = async (
    props: UploadChangeParam<UploadFile>,
    personIndex: number,
    side: AttachmentSide
  ) => {
    // Ant Upload with a no-op customRequest fires onChange for the "uploading"
    // and "done" transitions. Only act once, on "done", so we don't upload the
    // same file to the server twice.
    if (props.file?.status !== "done") return;

    const rcFile = props.fileList?.[0]?.originFileObj as RcFile | undefined;
    if (!rcFile) return;

    let base64Image = await getBase64(rcFile);

    const { fileName, bucketName } = await uploadImageServer({
      image: base64Image,
    });

    const field = ATTACHMENT_FIELD[side];
    const curPersons = form.getFieldValue("persons");
    let updatedPersons = curPersons.map((p: BusinessPerson, i: number) =>
      i === personIndex ? { ...p, [field]: { fileName, bucketName } } : p
    );
    form.setFieldsValue({ persons: updatedPersons });
  };
  const handleImagePreview = async (
    personIndex: number,
    side: AttachmentSide
  ) => {
    const person = form?.getFieldValue("persons")[personIndex];
    if (!person) return;

    const attachment = person[ATTACHMENT_FIELD[side]];
    if (!attachment?.fileName) return;

    const src = await getImageFromServer({
      fileName: attachment.fileName,
      bucketName: attachment.bucketName,
    });

    // Branch on document type: antd <Image> only renders images, so a PDF would
    // show a broken-image icon. Show PDFs in a Modal <iframe> instead. Both
    // viewers render in-page (no window.open after the await, which the browser
    // popup-blocks outside the click gesture).
    if (isPdfFileName(attachment.fileName)) {
      setPreviewPdf(src);
      return;
    }

    setPreviewImage(src);
    setPreviewOpen(true);
  };
  const dummyRequest = ({ file, onSuccess }: any) => {
    setTimeout(() => {
      onSuccess("ok");
    }, 0);
  };

  // Runs before Ant Upload accepts the file: reject anything that isn't a valid
  // identity document (type/size/non-empty), and warn when it would replace an
  // already-uploaded side. Returning Upload.LIST_IGNORE drops the file so no
  // upload / onChange fires.
  const beforeUpload = (file: RcFile, hasExistingFile: boolean) => {
    if (!ALLOWED_DOCUMENT_TYPES.includes(file.type)) {
      message.error("Unsupported file type. Upload a JPG, PNG, or PDF.");
      return Upload.LIST_IGNORE;
    }
    if (file.size === 0) {
      message.error("The selected file is empty.");
      return Upload.LIST_IGNORE;
    }
    if (file.size / 1024 / 1024 >= MAX_DOCUMENT_SIZE_MB) {
      message.error(
        `File is too large. Maximum size is ${MAX_DOCUMENT_SIZE_MB} MB.`
      );
      return Upload.LIST_IGNORE;
    }
    if (hasExistingFile) {
      message.warning(
        "This side already has a document — uploading a new file will replace it."
      );
    }
    return true;
  };

  const renderAttachmentUpload = (
    name: number,
    restField: object,
    side: AttachmentSide
  ) => {
    const field = ATTACHMENT_FIELD[side];
    const person = form?.getFieldValue("persons")?.[name];
    const hasFile = person?.[field]?.fileName;
    // Not enforced on save: a one-sided two-sided document is accepted, and the
    // Adyen panel reports which sides a submission would actually include.
    const sideRecommended = isTwoSidedDocumentType(
      person?.identificationDocumentType
    );
    const label = sideRecommended
      ? `${ATTACHMENT_LABEL[side]} (recommended)`
      : ATTACHMENT_LABEL[side];

    return (
      <Space>
        <label className={styles.custom_label}>{label}</label>
        <Upload
          name="logo"
          customRequest={dummyRequest}
          listType="picture"
          maxCount={1}
          beforeUpload={(file) => beforeUpload(file, Boolean(hasFile))}
          onChange={(props) => {
            handleUpload(props, name, side);
          }}
        >
          <Button type="primary" icon={<UploadOutlined />}>
            Click & upload
          </Button>
        </Upload>
        {hasFile && (
          <Button type="primary" onClick={() => handleImagePreview(name, side)}>
            <EyeOutlined />
          </Button>
        )}
        <Form.Item
          {...restField}
          name={[name, field, "fileName"]}
          hidden
          noStyle
        >
          <Input />
        </Form.Item>
        <Form.Item
          {...restField}
          name={[name, field, "bucketName"]}
          hidden
          noStyle
        >
          <Input />
        </Form.Item>
      </Space>
    );
  };

  const DEFAULT_NULL_ADDRESS_VALUE = "";

  return (
    <>
      {previewImage && (
        <Image
          style={{ display: "none" }}
          src={previewImage}
          preview={{
            visible: previewOpen,
            src: previewImage,
            onVisibleChange: (visible) => {
              setPreviewOpen(visible);
              if (!visible) setPreviewImage("");
            },
          }}
        />
      )}
      <Modal
        open={Boolean(previewPdf)}
        onCancel={() => setPreviewPdf("")}
        footer={null}
        width="80%"
        title="Document preview"
        destroyOnClose
      >
        {previewPdf && (
          <iframe
            title="Document preview"
            src={previewPdf}
            style={{ width: "100%", height: "80vh", border: "none" }}
          />
        )}
      </Modal>
      {adyenDocumentsError && (
        <AdyenDocumentStateAlert
          error={adyenDocumentsError}
          onRetry={refetchAdyenDocuments}
          style={{ marginBottom: 16 }}
        />
      )}
      <Form.List name="persons">
        {(fields, { add, remove }) => (
          <>
            {fields.map(({ key, name, ...restField }) => {
              return (
                <Space
                  key={key}
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    marginBottom: 30,
                  }}
                  align="baseline"
                >
                  <h3>
                    Person {name + 1}{" "}
                    {fields.length >= 1 ? (
                      <MinusCircleOutlined onClick={() => remove(name)} />
                    ) : null}
                  </h3>
                  <Space>
                    <label className={styles.custom_label}>First Name</label>
                    <Form.Item
                      {...restField}
                      name={[name, "firstName"]}
                      label="First Name"
                      noStyle
                      rules={[
                        {
                          required: true,
                          message: "Missing First Name",
                        },
                      ]}
                    >
                      <Input placeholder="First Name" />
                    </Form.Item>
                  </Space>

                  <Space>
                    <label className={styles.custom_label}>Last Name</label>
                    <Form.Item
                      {...restField}
                      name={[name, "lastName"]}
                      label="Last Name"
                      noStyle
                      rules={[
                        {
                          required: true,
                          message: "Missing Last Name",
                        },
                      ]}
                    >
                      <Input placeholder="Last Name" />
                    </Form.Item>{" "}
                  </Space>
                  <Space>
                    <label className={styles.custom_label}>Email</label>
                    <Form.Item
                      {...restField}
                      name={[name, "email"]}
                      noStyle
                      normalize={(value?: string) => value?.trim()}
                      rules={[personEmailRule(form, name)]}
                    >
                      <Input placeholder="Email" />
                    </Form.Item>
                  </Space>

                  <Space>
                    <label className={styles.custom_label}>Roles</label>
                    <Form.Item
                      {...restField}
                      name={[name, "roles"]}
                      noStyle
                      rules={[
                        {
                          required: true,
                          message: "Missing Roles",
                        },
                      ]}
                    >
                      <Select
                        mode="multiple"
                        placeholder="Roles"
                        style={{ width: "178px" }}
                      >
                        <Option value="PrimaryContact">Primary Contact</Option>
                        <Option value="Director">Director</Option>
                        <Option value="AuthorizedSignatory">
                          Authorized Signatory
                        </Option>
                        <Option value="BoardOfDirectors">
                          Board Of Directors
                        </Option>
                        <Option value="BeneficialOwner">
                          Beneficial Owner
                        </Option>
                        <Option value="VotingControl">Voting Control</Option>
                        <Option value="PersonShareOwner">
                          Person Share Owner
                        </Option>
                      </Select>
                    </Form.Item>
                  </Space>
                  <Space>
                    <label className={styles.custom_label}>Date Of Birth</label>
                    <Form.Item
                      {...restField}
                      name={[name, "dateOfBirth"]}
                      label="Date Of Birth"
                      noStyle
                      rules={[
                        {
                          required: true,
                          message: "Missing Date Of Birth",
                        },
                      ]}
                    >
                      <DatePicker format={dateFormat} />
                      {/* <Input placeholder="Date Of Birth" /> */}
                    </Form.Item>
                  </Space>
                  <Space>
                    <label className={styles.custom_label}>Street</label>
                    <Form.Item
                      {...restField}
                      name={[name, "address", "street"]}
                      initialValue={DEFAULT_NULL_ADDRESS_VALUE}
                      noStyle
                    >
                      <Input placeholder="Street Name" />
                    </Form.Item>
                  </Space>

                  <Space>
                    <label className={styles.custom_label}>House Number</label>
                    <Form.Item
                      {...restField}
                      initialValue={DEFAULT_NULL_ADDRESS_VALUE}
                      name={[name, "address", "houseNumber"]}
                      noStyle
                    >
                      <Input placeholder="House Number" />
                    </Form.Item>{" "}
                  </Space>
                  <Space>
                    <label className={styles.custom_label}>Postcode</label>
                    <Form.Item
                      {...restField}
                      initialValue={DEFAULT_NULL_ADDRESS_VALUE}
                      name={[name, "address", "postcode"]}
                      noStyle
                    >
                      <Input placeholder="Postcode" />
                    </Form.Item>{" "}
                  </Space>
                  <Space>
                    <label className={styles.custom_label}>City</label>
                    <Form.Item
                      {...restField}
                      initialValue={DEFAULT_NULL_ADDRESS_VALUE}
                      name={[name, "address", "city"]}
                      noStyle
                    >
                      <Input placeholder="City" />
                    </Form.Item>
                  </Space>
                  <Space>
                    <label className={styles.custom_label}>Country</label>
                    <Form.Item
                      {...restField}
                      name={[name, "address", "country"]}
                      initialValue={"NL"}
                      noStyle
                    >
                      <Input placeholder="Country" />
                    </Form.Item>
                  </Space>

                  <Space>
                    <label className={styles.custom_label}>Phone</label>
                    <Form.Item {...restField} label="Phone" noStyle>
                      <Space size={0}>
                        <Form.Item
                          name={[name, "phone", "countryCode"]}
                          initialValue={"31"}
                          {...restField}
                          noStyle
                        >
                          <Input style={{ width: "80px" }} addonBefore="+" />
                        </Form.Item>
                        <Form.Item
                          name={[name, "phone", "number"]}
                          noStyle
                          {...restField}
                        >
                          <Input style={{ width: "120px" }} />
                        </Form.Item>
                      </Space>
                    </Form.Item>
                  </Space>
                  <Space>
                    <label className={styles.custom_label}>Document type</label>
                    <Form.Item
                      {...restField}
                      name={[name, "identificationDocumentType"]}
                      noStyle
                    >
                      <Select
                        allowClear
                        placeholder="Optional"
                        style={{ width: "178px" }}
                        options={[...PERSON_IDENTITY_DOCUMENT_TYPE_OPTIONS]}
                      />
                    </Form.Item>
                  </Space>
                  <PersonAdyenDocuments
                    businessId={currentBusiness?._id}
                    personId={form.getFieldValue("persons")?.[name]?._id}
                  />
                  {renderAttachmentUpload(name, restField, "front")}
                  {renderAttachmentUpload(name, restField, "back")}
                </Space>
              );
            })}
            <Form.Item>
              <Space>
                <Button
                  type="dashed"
                  onClick={() => add()}
                  block
                  icon={<PlusOutlined />}
                >
                  Add Person
                </Button>
              </Space>
            </Form.Item>
          </>
        )}
      </Form.List>
    </>
  );
}
